import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SaveIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import CancelButton from '../../../../components/buttons/cancel-button.jsx';
import Error from '../../../../components/feedback/error.jsx';
import Loading from '../../../../components/feedback/loading.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import FormCard from '../../../../components/ui/form-card.jsx';
import FormFooter from '../../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form.jsx';
import { Input } from '../../../../components/ui/input.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { queryClient } from '../../../../services/query/client.js';

export function EligibilityWatchlistEdit() {
  const navigate = useNavigate();
  const { employeeId } = useParams();

  const formEditEligibilityWatchlist = useForm({
    defaultValues: {
      name: undefined,
      identificationDoc: undefined,
      shop: undefined,
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .min(4, 'Nome tem de ter pelo menos 4 caracteres')
          .max(60, 'Nome não pode ultrapassar 60 caracteres')
          .required('Nome é um campo obrigatório'),
        shop: yup.string().required('Loja é um campo obrigatório'),
      }),
    ),
  });

  const queryEligibilityWatchlist = useQuery({
    queryKey: ['update_eligibility-watchlist'],
    queryFn: async () => {
      const { data: eligibilityWatchlist } = await msCatalystApi.get(
        `/eligibility-watchlist/${employeeId}`,
      );

      formEditEligibilityWatchlist.reset({
        name: eligibilityWatchlist.data.name,
        identificationDoc: eligibilityWatchlist.data.identificationDoc,
        shop: eligibilityWatchlist.data.shop,
      });

      return eligibilityWatchlist.data;
    },
  });

  const mutationEditEligibilityWatchlist = useMutation({
    mutationFn: async (data) => {
      const { data: updatedEligibilityWatchlistRes } = await msCatalystApi.put(
        `/eligibility-watchlist/${employeeId}`,
        {
          ROWID: employeeId,
          ...data,
        },
      );

      return updatedEligibilityWatchlistRes.data;
    },
    onSuccess: (data) => {
      toast.success('Funcionário Interno / Loja editado(a) com successo!');
      queryClient.setQueryData(['eligibility-watchlist'], data);

      navigate(`/backoffice/admin/eligibility-watchlist/${data.ROWID}`);
    },
    onError: (err) => {
      if (err?.response?.data) {
        toast.error(
          `Erro ao editar Funcionário Interno / Loja: ${err.data.message}`,
          {
            description: 'Tente novamente mais tarde',
          },
        );
      } else {
        toast.error('Erro ao editar Funcionário Interno / Loja', {
          description: 'Tente novamente mais tarde',
        });
      }
    },
  });

  const onSubmit = async (data) => {
    mutationEditEligibilityWatchlist.mutate(data);
  };

  if (queryEligibilityWatchlist.isLoading) {
    return <Loading />;
  }

  if (queryEligibilityWatchlist.isError) {
    return <Error />;
  }

  return (
    <FormUnsavedConfirmationWrapper form={formEditEligibilityWatchlist}>
      <div className="pb-6">
        <Heading className="pb-2">Editar Funcionário Interno / Loja</Heading>
        <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
          {queryEligibilityWatchlist.data.name}
        </h2>
      </div>

      <Form {...formEditEligibilityWatchlist}>
        <form onSubmit={formEditEligibilityWatchlist.handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-10">
            <FormCard>
              <FormField
                control={formEditEligibilityWatchlist.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formEditEligibilityWatchlist.control}
                name="identificationDoc"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nº Identificação</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formEditEligibilityWatchlist.control}
                name="shop"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Loja</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormCard>
          </div>

          <FormFooter>
            <CancelButton />

            <Button
              type="submit"
              disabled={
                formEditEligibilityWatchlist.formState.isSubmitting ||
                !formEditEligibilityWatchlist.formState.isDirty
              }
              isLoading={
                formEditEligibilityWatchlist.formState.isSubmitting ||
                formEditEligibilityWatchlist.isPending
              }
            >
              <SaveIcon />
              Guardar Alterações
            </Button>
          </FormFooter>
        </form>
      </Form>
    </FormUnsavedConfirmationWrapper>
  );
}
